import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Menu, X, Sparkles, ExternalLink } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import { LOGO_URL } from "@/i18n";

export default function Navbar({ onOpenLead }) {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "services", label: t.nav.services },
    { id: "portfolio", label: t.nav.cases },
    { id: "about", label: t.nav.about },
    { id: "process", label: t.nav.process },
    { id: "blog", label: t.nav.blog, href: "https://reinventemercadeo.com/blog/", external: true },
  ];

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "py-3" : "py-5"
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto max-w-7xl px-5">
        <div
          className={`flex items-center justify-between rounded-2xl px-4 md:px-5 py-2.5 transition-all glass-strong shadow-[0_10px_40px_-12px_rgba(0,0,0,0.65)] ring-1 ring-white/12 ${
            scrolled ? "backdrop-blur-2xl" : "backdrop-blur-xl"
          }`}
        >
          <a href="#top" className="flex items-center gap-2.5 group" data-testid="navbar-logo">
            <img
              src={LOGO_URL}
              alt="Reinvente"
              className="h-9 w-9 rounded-full ring-1 ring-white/15 group-hover:ring-white/30 transition"
            />
            <div className="flex flex-col leading-none">
              <span className="font-display text-[15px] font-extrabold tracking-tight text-white [text-shadow:0_1px_8px_rgba(0,0,0,0.5)]">
                Reinvente
              </span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/65 font-mono-tight [text-shadow:0_1px_6px_rgba(0,0,0,0.5)]">
                Mercadeo · AI
              </span>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <a
                key={l.id}
                href={l.href ?? `#${l.id}`}
                {...(l.external ? { target: "_blank", rel: "noreferrer" } : {})}
                data-testid={`nav-link-${l.id}`}
                className="inline-flex items-center gap-1 px-3.5 py-2 text-[14px] font-bold text-white/95 hover:text-white rounded-lg hover:bg-white/10 transition-colors [text-shadow:0_1px_8px_rgba(0,0,0,0.45)]"
              >
                {l.label}
                {l.external && <ExternalLink className="h-3 w-3 opacity-50" />}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setLang(lang === "es" ? "en" : "es")}
              data-testid="lang-toggle"
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold text-white hover:bg-white/15 border border-white/20 hover:border-white/35 transition [text-shadow:0_1px_6px_rgba(0,0,0,0.45)]"
            >
              <Globe className="h-3.5 w-3.5" strokeWidth={1.75} />
              <span className="font-mono-tight uppercase">{lang}</span>
            </button>

            <button
              onClick={onOpenLead}
              data-testid="nav-cta-button"
              className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold text-white btn-primary-glow"
            >
              <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
              {t.nav.cta}
            </button>

            <button
              className="lg:hidden p-2 rounded-lg text-white/80 hover:bg-white/5"
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
              aria-label="menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="lg:hidden mt-2 glass-strong rounded-2xl p-3 flex flex-col"
              data-testid="mobile-menu"
            >
              {links.map((l) => (
                <a
                  key={l.id}
                  href={l.href ?? `#${l.id}`}
                  {...(l.external ? { target: "_blank", rel: "noreferrer" } : {})}
                  onClick={() => !l.external && setOpen(false)}
                  className="inline-flex items-center gap-1.5 px-4 py-3 text-sm text-white/80 hover:bg-white/5 rounded-lg"
                  data-testid={`mobile-nav-${l.id}`}
                >
                  {l.label}
                  {l.external && <ExternalLink className="h-3 w-3 opacity-50" />}
                </a>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  onOpenLead();
                }}
                className="mt-2 px-4 py-3 rounded-xl text-sm font-semibold text-white btn-primary-glow"
                data-testid="mobile-cta"
              >
                {t.nav.cta}
              </button>
              <button
                onClick={() => setLang(lang === "es" ? "en" : "es")}
                className="mt-2 px-4 py-2 rounded-xl text-xs text-white/70 border border-white/10"
                data-testid="mobile-lang-toggle"
              >
                <span className="font-mono-tight uppercase">{lang}</span> · {t.nav.switch}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
}
